import React, { useState, useRef, useEffect } from 'react';
import styles from './Popover.module.scss';
import { useFloating, offset, flip, shift, autoUpdate } from '@floating-ui/react';

interface PopoverProps {
  trigger: React.ReactNode;
  children: (onClose: () => void) => React.ReactNode;
  className?: string;
}

const Popover: React.FC<PopoverProps> = ({ trigger, children, className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);

  // Floating UI positioning
  const { refs, update, floatingStyles } = useFloating({
    placement: 'bottom-end',
    middleware: [offset(4), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  // Attach refs
  useEffect(() => {
    if (triggerRef.current) {
      refs.setReference(triggerRef.current);
    }
  }, [refs, triggerRef]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        refs.floating.current &&
        !refs.floating.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, refs.floating]);

  const togglePopover = () => {
    setIsOpen((open) => !open);
    setTimeout(() => {
      if (update) update();
    }, 0);
  };

  const closePopover = () => setIsOpen(false);

  return (
    <span className={`${styles.popoverContainer} ${className || ''}`}>
      <span
        className={styles.popoverTrigger}
        onClick={togglePopover}
        ref={triggerRef}
        tabIndex={0}
        role="button"
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        {trigger}
      </span>
      {isOpen && (
        <div
          ref={refs.setFloating}
          className={styles.menu}
          style={{ ...floatingStyles, zIndex: 1000 }}
        >
          {children(closePopover)}
        </div>
      )}
    </span>
  );
};

export default Popover;
